import { useCallback, useEffect, useId, useRef, useState } from "react";
import { Box, Typography } from "@mui/material";
import OpenSeadragon from "openseadragon";
import { Layer, Line, Rect, Stage, Text } from "react-konva";
import { color } from "../../theme/tokens/color.js";
import { effectiveWheelZoomFactor } from "./wheelZoomMath.js";
import { marqueeIsDragGesture } from "./marqueeZoom.js";
import { getGpuCapabilities } from "./gpuCapabilities.js";

/**
 * How long an `open-failed` waits before its error overlay is shown. A
 * transient failure (e.g. the prefetcher saturating the connection pool right
 * as the user navigates) is normally resolved within a beat by the full-res
 * `open()` succeeding, which cancels the pending error — so the user sees the
 * image, not a flash of "Couldn't load image". Only a failure that nothing
 * recovers from within this window surfaces.
 */
const ERROR_SURFACE_GRACE_MS = 3000;

/**
 * SETTINGS-02 — per-box appearance, resolved by the caller from the
 * user's per-kind bounding-box preferences. Every field is optional; the
 * Viewer falls back to the legacy hard-coded constants when a field (or
 * the whole `style`) is absent, so callers that don't pass style render
 * exactly as before.
 */
export interface BoundingBoxLabelStyleResolved {
  show?: boolean;
  /** When true, the label only renders for the selected box. */
  selectedOnly?: boolean;
  fontSize?: number;
  textColor?: string;
  /** Resolved hex; when omitted the box colour is used as the bg. */
  bgColor?: string;
  bgOpacity?: number;
  position?: "above" | "inside" | "auto";
}
export interface BoundingBoxStyle {
  strokeWidth?: number;
  activeStrokeWidth?: number;
  color?: string;
  activeColor?: string;
  fillOpacity?: number;
  activeFillOpacity?: number;
  /** Contrasting dark halo so the box reads on any background. */
  outline?: boolean;
  label?: BoundingBoxLabelStyleResolved;
}

export interface BoundingBox {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
  color?: string;
  label?: string;
  style?: BoundingBoxStyle;
}

export interface ViewportState {
  zoom: number;
  centerX: number;
  centerY: number;
}

export interface ViewerProps {
  imageUrl: string | null;
  boundingBoxes?: BoundingBox[];
  /** Id of a box to render with the highlighted style (thicker stroke + accent colour). */
  selectedBoxId?: string | null;
  onBoxClick?: (id: string) => void;
  renderer?: "konva" | "pixi";
  ariaLabel?: string;
  /** Story 012 — restore zoom + centre on `open`. Applied once per image
   *  load, then user interaction takes over. */
  initialViewport?: ViewportState | null;
  /** Story 012 — fires on every OSD `viewport-change` so the host can
   *  persist the zoom + centre per imageId. */
  onViewportChange?: (state: ViewportState) => void;
  /**
   * Backup URL retried automatically when OSD's `open-failed` fires on
   * the primary `imageUrl`. Used by the analysis page to ship `urls.full`
   * as the primary and `urls.thumb900` as the fallback — when `full_res`
   * isn't on disk locally, the BE returns a placeholder PNG that OSD
   * either fails to open OR renders as a 512×512 grey square; either
   * way the FE wants to step down to the always-present thumb_900.
   */
  fallbackImageUrl?: string | null;
  /**
   * Christian's brief 2026-05-10 §"loading seems take quite some time":
   * pass `imageUrl = urls.thumb900` (small + already cached) as the
   * primary so OSD shows pixels instantly, then `progressiveImageUrl =
   * urls.full` to upgrade once the full JPEG decode finishes. The
   * upgrade is silent — OSD `open()` swaps the tile source in place
   * keeping zoom + center.
   */
  progressiveImageUrl?: string | null;
  /**
   * R58-L-17 — cap for drag-rectangle + mouse-wheel zoom expressed as
   * a percentage of fit-to-viewport. Default 500. The Viewer treats it
   * as the hard ceiling and never zooms past `maxZoomPercent / 100` ×
   * fit-zoom.
   */
  maxZoomPercent?: number;
  /**
   * R58-L-15 — fires on every cursor move inside the wrapper with the
   * cursor's image-pixel coordinates (rounded), or `null` when the
   * cursor leaves the viewer.
   */
  onCursorImagePixel?: (pixel: { u: number; v: number } | null) => void;
  /**
   * Story 032 — fires on every cursor move with the cursor position as a
   * *fraction* of the image (`nx`, `ny` ∈ [0, 1]), or `null` on leave.
   * Resolution-independent: the fraction is the same whether the thumbnail
   * or the full-resolution image is currently loaded (it's derived from the
   * loaded image's content size), so the pic2comp radius readout stays stable
   * across the progressive thumb→full upgrade. `onCursorImagePixel` keeps
   * publishing raw loaded-image pixels for the debug overlay.
   */
  onCursorImageNormalized?: (norm: { nx: number; ny: number } | null) => void;
  /**
   * R58-L-15 — fires on every OSD `zoom`/`viewport-change` event with
   * the *percentage relative to fit-to-viewport* (so 100 = fit-to-
   * viewport, 500 = 5× zoomed in). Floors at 100.
   */
  onZoomPercentChange?: (zoomPercent: number) => void;
  /**
   * ANA-R3-NAV-01 — when provided, the four arrow keys stop panning the image
   * and instead step the host's data-point selection: Up / Left →
   * `"previous"`, Down / Right → `"next"`. Every other key (zoom, etc.) keeps
   * OSD's default behaviour. When omitted, arrows keep OSD's built-in pan, so
   * other consumers of the primitive are unaffected. The Viewer stays
   * domain-agnostic — the caller decides what "previous / next" means.
   */
  onArrowNavigate?: (direction: "previous" | "next") => void;
  /**
   * SETTINGS-02 — when true, bbox labels are auto-hidden while many boxes
   * crowd the viewport, and reappear as the user zooms in (fewer boxes in
   * view). Default false (labels always honour their per-box `show`).
   */
  autoHideLabelsWhenDense?: boolean;
  /**
   * SETTINGS-02 — optional notice rendered top-centre while labels are
   * auto-hidden by the dense-declutter rule (e.g. "Many bounding boxes —
   * labels hidden automatically"). The Viewer stays domain-agnostic; the
   * caller supplies the copy.
   */
  denseLabelNotice?: string;
  /**
   * ANA-R3-GEOM-03 — free-form polygon/polyline outlines to render over the
   * image, projected through the SAME OSD viewport transform as `boundingBoxes`.
   * Each `path` is a flat `[x0,y0,…]` array NORMALISED 0..1 (matching how the
   * GEOM-01 read exposes `Defect.path`). Closed shapes (first==last, or
   * `closed:true`) render as a filled `<Line>`; open ones as a stroked line.
   * Callers fall back to a derived `bbox` in `boundingBoxes` when a defect has
   * no `path` (AC4) — a defect should appear in exactly ONE of the two lists.
   */
  polygonOverlays?: ViewerPolygon[];
  /**
   * ANA-R3-GEOM-03 — exposes the live OSD viewport projection so a draw overlay
   * (the shared PolygonEditor) can build a `CoordinateTransform`. The Viewer
   * owns OSD privately; this is the only seam through which the draw layer can
   * map normalised image points ⇄ screen pixels and learn when the view
   * pans/zooms (`version` bumps on every OSD open / viewport-change / resize).
   * Receives `null` on unmount.
   */
  onViewportApiReady?: (api: ViewerViewportApi | null) => void;
}

/**
 * ANA-R3-GEOM-03 — a free-form outline to render over the image. `path` is a
 * flat `[x0,y0,…]` array in NORMALISED 0..1 image space. `closed` forces fill;
 * when omitted the Viewer infers closure from first==last.
 */
export interface ViewerPolygon {
  id: string;
  /** Flat `[x0,y0,…]`, normalised 0..1. */
  path: number[];
  /** Force closed (fill). When undefined, inferred from first==last. */
  closed?: boolean;
  color?: string;
  label?: string;
  style?: BoundingBoxStyle;
}

/**
 * ANA-R3-GEOM-03 — the OSD viewport projection seam handed to a draw overlay.
 * All coordinates are NORMALISED 0..1 on the image side and CSS pixels relative
 * to the viewer wrapper on the screen side (the same origin OSD's
 * `imageToViewerElementCoordinates` uses). `version` bumps on every view change
 * so a consumer's `CoordinateTransform` re-projects.
 */
export interface ViewerViewportApi {
  /** Normalised 0..1 image point → screen CSS px (relative to the wrapper). */
  imageToScreen(p: { x: number; y: number }): { x: number; y: number };
  /** Screen CSS px → normalised 0..1 image point. */
  screenToImage(p: { x: number; y: number }): { x: number; y: number };
  /** Monotonic token; bumps on OSD open / viewport-change / resize. */
  version: number;
}

interface ScreenBox extends BoundingBox {
  screenX: number;
  screenY: number;
  screenWidth: number;
  screenHeight: number;
}

/** ANA-R3-GEOM-03 — a polygon overlay projected to screen-space CSS px. */
interface ScreenPolygon {
  id: string;
  /** Flat `[sx0,sy0,…]` in wrapper-relative CSS px. */
  screenFlat: number[];
  closed: boolean;
  color?: string | undefined;
  label?: string | undefined;
  style?: BoundingBoxStyle | undefined;
}

// Story 031 — high-contrast bbox stroke.
//
// Konva draws to a <canvas>, so CSS custom properties are invisible here — a
// `var(--x)` string paints nothing. Every value below is therefore a REAL hex
// read off the token set at module scope.
//
// The set is `color.dark` in both themes, and not because this primitive is
// dark-only: the ground under these marks is the PHOTOGRAPH, on a calibrated
// near-black canvas that does not follow the page. Reading `color.light` when
// the app is in light mode would tune a mark against a surface it never
// touches. The lyse bbox preference defaults use the same set for the same
// reason, so a style-less caller and a default-styled one match.
//
// D84 series 4 (attention amber) keeps the previous orange's job — it was
// picked to read on a white blade AND a dark sky, and the amber measures 8.35
// on ink-900 while staying warm enough to separate from the blade. Selection is
// the accent: P3 makes aqua the only hue that may signal current position.
export const DEFAULT_BOX_COLOR = color.dark.series[3];
export const SELECTED_BOX_COLOR = color.dark.primary;
const DEFAULT_BOX_STROKE_WIDTH = 2;
const SELECTED_BOX_STROKE_WIDTH = 4;
// Legacy selected-box fill (when no per-box style overrides it) — the same
// accent at 12 %, derived rather than restated so the pair can never drift.
const SELECTED_BOX_FILL = hexToRgba(SELECTED_BOX_COLOR, 0.12);
/**
 * The contrasting halo behind a box + the scrim behind the overlay chrome.
 * `neutralDeep` is the system's deepest neutral (`--ink-900`); pure black is
 * not a token in this system and never was a measured value here.
 */
const CANVAS_SCRIM = color.dark.neutralDeep;
/** The one neutral that reads on the canvas — labels, marquee, notices. */
const CANVAS_ON_SCRIM = color.dark.text.primary;

// SETTINGS-02 — dense-declutter threshold. When more than this many boxes
// fall within the viewport AND `autoHideLabelsWhenDense` is on, all labels
// are hidden until the analyst zooms in enough to drop below it.
const DENSE_LABEL_VIEWPORT_THRESHOLD = 30;

/** `#RRGGBB` (+ optional alpha) → `rgba(r,g,b,a)`. Falls back to the raw
 *  input when it isn't a 6-digit hex (e.g. already an rgba/named colour). */
function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-fA-F]{6})$/.exec(hex.trim());
  if (!m?.[1]) return hex;
  const int = parseInt(m[1], 16);
  const r = (int >> 16) & 255;
  const g = (int >> 8) & 255;
  const b = int & 255;
  return `rgba(${String(r)}, ${String(g)}, ${String(b)}, ${String(alpha)})`;
}

// ANA-R3-GEOM-03 — is a flat `[x0,y0,…]` ring closed (first vertex == last)?
function isClosedFlat(flat: readonly number[]): boolean {
  if (flat.length < 6) return false; // need ≥3 pts to be a meaningful ring
  const x0 = flat[0];
  const y0 = flat[1];
  const xn = flat[flat.length - 2];
  const yn = flat[flat.length - 1];
  if (
    typeof x0 !== "number" ||
    typeof y0 !== "number" ||
    typeof xn !== "number" ||
    typeof yn !== "number"
  ) {
    return false;
  }
  return Math.abs(x0 - xn) < 1e-6 && Math.abs(y0 - yn) < 1e-6;
}

// ANA-R3-GEOM-03 — normalised 0..1 image point → OSD image-pixel point, using
// the loaded content size (8k full-res or 900px thumb — same fraction either
// way). Falls back to treating the input as already image-pixel when the world
// isn't loaded yet (jsdom / pre-open), which the caller's try/catch tolerates.
function normalisedToImagePoint(
  viewer: OpenSeadragon.Viewer,
  nx: number,
  ny: number,
): OpenSeadragon.Point {
  let w = 1;
  let h = 1;
  try {
    if (viewer.world.getItemCount() > 0) {
      const size = viewer.world.getItemAt(0).getContentSize();
      if (size.x > 0 && size.y > 0) {
        w = size.x;
        h = size.y;
      }
    }
  } catch {
    // jsdom: ignore — fall back to the unit square.
  }
  return new OpenSeadragon.Point(nx * w, ny * h);
}

// ANA-R3-GEOM-03 — build the draw-overlay viewport projection seam over a live
// OSD viewer. `imageToScreen`/`screenToImage` are exact inverses through OSD's
// element↔image transforms, mapping normalised 0..1 ⇄ wrapper-relative CSS px.
function makeViewportApi(
  viewer: OpenSeadragon.Viewer,
  version: number,
): ViewerViewportApi {
  const contentSize = (): { w: number; h: number } => {
    try {
      if (viewer.world.getItemCount() > 0) {
        const size = viewer.world.getItemAt(0).getContentSize();
        if (size.x > 0 && size.y > 0) return { w: size.x, h: size.y };
      }
    } catch {
      // ignore
    }
    return { w: 1, h: 1 };
  };
  return {
    version,
    imageToScreen: (pt) => {
      const { w, h } = contentSize();
      try {
        const sp = viewer.viewport.imageToViewerElementCoordinates(
          new OpenSeadragon.Point(pt.x * w, pt.y * h),
        );
        return { x: sp.x, y: sp.y };
      } catch {
        return { x: pt.x * w, y: pt.y * h };
      }
    },
    screenToImage: (pt) => {
      const { w, h } = contentSize();
      try {
        const ip = viewer.viewport.viewerElementToImageCoordinates(
          new OpenSeadragon.Point(pt.x, pt.y),
        );
        return { x: w === 0 ? 0 : ip.x / w, y: h === 0 ? 0 : ip.y / h };
      } catch {
        return { x: 0, y: 0 };
      }
    },
  };
}

// Story 032 / SB6-ANA-VIEW-01-FIX — the right-click marquee commits a
// zoom whenever the gesture is an intentional DRAG (see
// `marqueeIsDragGesture`); the resulting zoom is clamped to the user's
// `maxZoomPercent` by OSD's `fitBoundsWithConstraints`. A near-zero
// click never commits (R58-L-16 AC3). The previous adaptive min-size
// (`viewer.width / maxZoomFactor`, ≈160 px at 500 %) rejected normal
// small boxes outright — the SB6 regression Christian reported — and
// violated R58-L-16 AC2 ("clamp to max", not "no-op").

function StubRegion({
  ariaLabel,
  children,
}: {
  ariaLabel: string;
  children: React.ReactNode;
}) {
  return (
    <Box
      role="region"
      aria-label={ariaLabel}
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: 320,
        bgcolor: "background.default",
        border: 1,
        borderColor: "divider",
        borderRadius: 1,
        p: 4,
      }}
    >
      {children}
    </Box>
  );
}

export function Viewer({
  imageUrl,
  boundingBoxes = [],
  selectedBoxId = null,
  onBoxClick,
  renderer = "konva",
  ariaLabel = "Image viewer",
  initialViewport = null,
  onViewportChange,
  fallbackImageUrl = null,
  progressiveImageUrl = null,
  maxZoomPercent = 500,
  onCursorImagePixel,
  onCursorImageNormalized,
  onZoomPercentChange,
  onArrowNavigate,
  autoHideLabelsWhenDense = false,
  denseLabelNotice,
  polygonOverlays = [],
  onViewportApiReady,
}: ViewerProps) {
  if (renderer === "pixi") {
    return (
      <StubRegion ariaLabel={ariaLabel}>
        <Typography color="text.secondary">
          Pixi renderer not implemented yet (story 003b).
        </Typography>
      </StubRegion>
    );
  }

  if (!imageUrl) {
    return (
      <StubRegion ariaLabel={ariaLabel}>
        <Typography color="text.secondary">No image available</Typography>
      </StubRegion>
    );
  }

  return (
    <KonvaViewer
      imageUrl={imageUrl}
      boundingBoxes={boundingBoxes}
      selectedBoxId={selectedBoxId}
      onBoxClick={onBoxClick}
      ariaLabel={ariaLabel}
      initialViewport={initialViewport}
      onViewportChange={onViewportChange}
      fallbackImageUrl={fallbackImageUrl}
      progressiveImageUrl={progressiveImageUrl}
      maxZoomPercent={maxZoomPercent}
      onCursorImagePixel={onCursorImagePixel}
      onCursorImageNormalized={onCursorImageNormalized}
      onZoomPercentChange={onZoomPercentChange}
      onArrowNavigate={onArrowNavigate}
      autoHideLabelsWhenDense={autoHideLabelsWhenDense}
      denseLabelNotice={denseLabelNotice}
      polygonOverlays={polygonOverlays}
      onViewportApiReady={onViewportApiReady}
    />
  );
}

interface KonvaViewerProps {
  imageUrl: string;
  boundingBoxes: BoundingBox[];
  selectedBoxId: string | null;
  onBoxClick: ((id: string) => void) | undefined;
  ariaLabel: string;
  initialViewport: ViewportState | null;
  onViewportChange: ((state: ViewportState) => void) | undefined;
  fallbackImageUrl: string | null;
  progressiveImageUrl: string | null;
  maxZoomPercent: number;
  onCursorImagePixel:
    | ((pixel: { u: number; v: number } | null) => void)
    | undefined;
  onCursorImageNormalized:
    | ((norm: { nx: number; ny: number } | null) => void)
    | undefined;
  onZoomPercentChange: ((zoomPercent: number) => void) | undefined;
  onArrowNavigate: ((direction: "previous" | "next") => void) | undefined;
  autoHideLabelsWhenDense: boolean;
  denseLabelNotice: string | undefined;
  polygonOverlays: ViewerPolygon[];
  onViewportApiReady: ((api: ViewerViewportApi | null) => void) | undefined;
}

interface MarqueeRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

function KonvaViewer({
  imageUrl,
  boundingBoxes,
  selectedBoxId,
  onBoxClick,
  ariaLabel,
  initialViewport,
  onViewportChange,
  fallbackImageUrl,
  progressiveImageUrl,
  maxZoomPercent,
  onCursorImagePixel,
  onCursorImageNormalized,
  onZoomPercentChange,
  onArrowNavigate,
  autoHideLabelsWhenDense,
  denseLabelNotice,
  polygonOverlays,
  onViewportApiReady,
}: KonvaViewerProps) {
  const containerId = useId().replace(/[^A-Za-z0-9_-]/g, "-");
  const containerRef = useRef<HTMLDivElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const viewerRef = useRef<OpenSeadragon.Viewer | null>(null);
  const [size, setSize] = useState<{ width: number; height: number }>({
    width: 0,
    height: 0,
  });
  const [screenBoxes, setScreenBoxes] = useState<ScreenBox[]>([]);
  const [screenPolygons, setScreenPolygons] = useState<ScreenPolygon[]>([]);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Story 032 — drag-to-zoom marquee. Right-mouse-down + drag draws a
  // dashed cyan rectangle (CSS px relative to the wrapper); release
  // commits the zoom. Escape cancels.
  const [marquee, setMarquee] = useState<MarqueeRect | null>(null);
  // R58-L-16 — "is the user actively panning?" → drives the grabbing
  // cursor. True while Space is held OR a middle-mouse drag is in
  // progress. State (not ref) because we need to re-render the wrapper
  // for the CSS attribute selector.
  const [panActive, setPanActive] = useState<boolean>(false);

  // Pin callback + initialViewport identities so the OSD-mount effect
  // doesn't tear down + rebuild the viewer every time the parent
  // re-renders (callbacks defined inline in JSX get a fresh identity
  // per render, which used to destroy OSD before the JPEG finished
  // loading — root cause of "Viewer never shows full-res").
  const onViewportChangeRef = useRef(onViewportChange);
  const initialViewportRef = useRef(initialViewport);
  // R58-L-15/L-16/L-17 — additional ref-tracked props consumed by the
  // OSD-mount effect (and the wheel/pan handlers). Refs so the mount
  // effect isn't re-keyed on prop identity churn.
  const maxZoomPercentRef = useRef(maxZoomPercent);
  const onCursorImagePixelRef = useRef(onCursorImagePixel);
  const onCursorImageNormalizedRef = useRef(onCursorImageNormalized);
  const onZoomPercentChangeRef = useRef(onZoomPercentChange);
  // ANA-R3-NAV-01 — ref-tracked so the OSD-mount effect (which runs once) can
  // read the latest callback inside its keyDownHandler override without being
  // re-keyed on the callback's identity.
  const onArrowNavigateRef = useRef(onArrowNavigate);
  useEffect(() => {
    onViewportChangeRef.current = onViewportChange;
  }, [onViewportChange]);
  useEffect(() => {
    initialViewportRef.current = initialViewport;
  }, [initialViewport]);
  useEffect(() => {
    maxZoomPercentRef.current = maxZoomPercent;
  }, [maxZoomPercent]);
  useEffect(() => {
    onCursorImagePixelRef.current = onCursorImagePixel;
  }, [onCursorImagePixel]);
  useEffect(() => {
    onCursorImageNormalizedRef.current = onCursorImageNormalized;
  }, [onCursorImageNormalized]);
  useEffect(() => {
    onZoomPercentChangeRef.current = onZoomPercentChange;
  }, [onZoomPercentChange]);
  useEffect(() => {
    onArrowNavigateRef.current = onArrowNavigate;
  }, [onArrowNavigate]);
  // ANA-R3-GEOM-03 — ref-track the viewport-api callback + a monotonic version
  // the projection effect bumps so a draw overlay's CoordinateTransform
  // re-projects on every view change.
  const onViewportApiReadyRef = useRef(onViewportApiReady);
  const viewportVersionRef = useRef(0);
  useEffect(() => {
    onViewportApiReadyRef.current = onViewportApiReady;
  }, [onViewportApiReady]);

  // R58-L-16 — refs for pan state. `spaceDownRef` tracks the Space key
  // (we toggle the cursor + intercept left-button-drag while it's held);
  // `middlePanRef` tracks middle-mouse drag. Both paths converge on
  // OSD's `viewport.panBy` so the constraints + edge-stop work uniformly.
  const spaceDownRef = useRef(false);
  const middlePanRef = useRef<{ x: number; y: number } | null>(null);
  const spacePanRef = useRef<{ x: number; y: number } | null>(null);
  // R58-L-16 — last contextmenu timestamp for the double-right-click
  // reset gesture. Two contextmenu events within 350 ms == "reset".
  const lastContextMenuRef = useRef<number>(0);
  const DBL_RCLICK_WINDOW_MS = 350;

  // OSD is mounted ONCE (below) and kept for the component's lifetime; the URL
  // props are swapped into the live viewer by a separate sync effect rather
  // than tearing OSD down and rebuilding it per image. These refs let the
  // once-bound handlers + the sync effect read the CURRENT URL props without
  // re-keying the mount effect. `openedPrimaryRef` is the imageUrl currently
  // opened as OSD's primary source, so the sync effect skips a redundant
  // re-open (notably on the first run, which matches the constructor).
  const imageUrlRef = useRef(imageUrl);
  const fallbackImageUrlRef = useRef(fallbackImageUrl);
  const fallbackTriedRef = useRef(false);
  const openedPrimaryRef = useRef(imageUrl);
  // The URL most recently handed to `viewer.open()` (or the constructor). The
  // progressive thumb900→full upgrade issues a second `open()` that ABORTS the
  // still-loading primary, and OSD fires `open-failed` for that aborted source.
  // Comparing the failed source against this ref lets us ignore those
  // superseded failures (the full-res source is what we now want shown) and
  // only surface a genuine failure of the current source.
  const latestOpenUrlRef = useRef(imageUrl);
  // Pending error-surfacing timer. An `open-failed` does NOT show the overlay
  // immediately — it schedules it. A *transient* thumb900 failure (the
  // prefetcher saturating the connection pool right as the user navigates) is
  // routinely followed within a beat by the full-res `open()` succeeding; that
  // success cancels the pending error, so the user never sees a flash. The
  // overlay only appears if nothing opens within the grace window — i.e. a
  // genuinely unloadable image. Holds a numeric timer id (browser setTimeout).
  const errorTimerRef = useRef<number | null>(null);

  // Cancel any pending error timer and clear a shown one. A ref so the
  // mount-once + URL-sync effects can call it without becoming a dependency
  // (its identity is irrelevant — it only touches the stable timer ref and the
  // stable setErrorMsg setter).
  const dismissErrorRef = useRef<() => void>(() => undefined);
  dismissErrorRef.current = () => {
    if (errorTimerRef.current !== null) {
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
    setErrorMsg(null);
  };

  // Mount OpenSeadragon ONCE. The viewer instance and its event handlers
  // persist; navigating between images swaps the tile source in place (the
  // URL-sync effect below) instead of destroying + recreating OSD. The old
  // code re-keyed this effect on the URL trio, so every arrow-press tore down
  // the canvas/GL context, re-ran the innerTracker fix, and re-bound every
  // handler — wasteful churn that also raced the cache hook's blob: URLs.
  useEffect(() => {
    const node = containerRef.current;
    if (!node) return;

    // PERF-GPU-01 (decision N-7) — detect the device max-texture capability
    // once per session at viewer init. Memoised module-level: the first
    // viewer mount performs the detection + emits the single structured
    // `[gpu-caps]` telemetry line; the host chrome reads the same memoised
    // value for the per-image downscale warning.
    getGpuCapabilities();

    const viewer = OpenSeadragon({
      element: node,
      tileSources: { type: "image", url: imageUrlRef.current },
      showNavigator: false,
      showZoomControl: false,
      showHomeControl: false,
      showFullPageControl: false,
      // R58-L-16 — disable OSD's built-in mouse wheel handler. We
      // intercept `wheel` ourselves on the wrapper so we can (a) cap at
      // `maxZoomPercent` (R58-L-17), (b) floor at fit-to-viewport, and
      // (c) bypass when Ctrl is held so the browser's page-zoom keeps
      // working (AC11). Disable the built-in click + double-click zoom
      // too — we drive every zoom path through our handlers.
      gestureSettingsMouse: {
        clickToZoom: false,
        dblClickToZoom: false,
        scrollToZoom: false,
      },
      // R58-L-17 — pin max zoom to `maxZoomPercent / 100` × fit-zoom.
      // OSD's `maxZoomLevel` is absolute (not relative to home).
      // `defaultZoomLevel: 0` opens at the home (contain-fit) zoom.
      //
      // SB6-ANA-POP-01 (Christian 2026-05-27) — floor zoom-out at the
      // CONTAIN-fit (`goHome`) zoom, not at OSD zoom `1.0`. Zoom `1.0`
      // means "image width fills the viewport width"; when the window is
      // wider/narrower than the image that crops the top/bottom, so a
      // hard `minZoomLevel: 1` stopped the wheel before the whole image
      // was visible (while double-right-click `goHome()` showed all of
      // it). `minZoomImageRatio: 1` (with no absolute `minZoomLevel`)
      // makes OSD's min zoom exactly `getHomeZoom()` — the same contain
      // fit — so wheel-out now bottoms out where `goHome` does. Wheel-up
      // still clamps at `maxZoomLevel` (AC10).
      defaultZoomLevel: 0,
      minZoomImageRatio: 1,
      maxZoomLevel: Math.max(1, maxZoomPercentRef.current / 100),
      // Constrain pan to image bounds — AC7 (pan stops at edges).
      visibilityRatio: 1,
      constrainDuringPan: true,
      // The internal canvas is aria-hidden; remove from tab order so axe
      // doesn't flag "focusable content inside aria-hidden". Keyboard control
      // still reaches OpenSeadragon through the wrapper's tabIndex={0}.
      tabIndex: -1,
      animationTime:
        typeof window !== "undefined" &&
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? 0
          : 0.5,
      crossOriginPolicy: "Anonymous",
    });
    viewerRef.current = viewer;

    // SB6-ANA-VIEW-01-FIX — the load-bearing fix for "right-click zoom
    // does nothing on a real image". OpenSeadragon's innerTracker calls
    // `preventDefault()` on the SECONDARY-button `pointerdown`/`pointerup`
    // whenever its `nonPrimary*` handlers are set (see OSD
    // updatePointerDown / updatePointerUp). Per the Pointer Events spec,
    // `preventDefault()` on a `pointerdown` suppresses the browser's
    // compatibility `mousedown`/`mousemove`/`mouseup` for that gesture —
    // which silently starved the marquee zoom of the very mouse events it
    // listens for. (It only ever "worked" under Playwright, which
    // dispatches mouse events directly, and never against a real
    // interactive image.) We don't use OSD's nonPrimary press/release
    // events, so clear them; the compat mouse events then fire and the
    // right-drag marquee + middle-mouse pan work. The narrow cast is
    // because the DefinitelyTyped MouseTracker types the handlers as
    // required functions, not nullable.
    try {
      const innerTracker = (
        viewer as unknown as {
          innerTracker?: {
            nonPrimaryPressHandler: unknown;
            nonPrimaryReleaseHandler: unknown;
          };
        }
      ).innerTracker;
      if (innerTracker) {
        innerTracker.nonPrimaryPressHandler = null;
        innerTracker.nonPrimaryReleaseHandler = null;
      }
    } catch {
      // jsdom / stub paths: ignore.
    }

    // ANA-R3-NAV-01 — reassign the four arrow keys from OSD's built-in
    // image-pan to the host's data-point navigation. We wrap OSD's
    // `innerTracker.keyDownHandler` (same poke as the nonPrimary fix above):
    // when `onArrowNavigate` is set, an arrow sets `preventDefaultAction`
    // (so OSD does NOT pan) and calls back with the direction; every other
    // key delegates to OSD's original handler unchanged (zoom etc. intact).
    // When `onArrowNavigate` is absent we never intercept, so OSD's default
    // arrow-pan is preserved for other consumers of the primitive.
    try {
      const keyTracker = (
        viewer as unknown as {
          innerTracker?: {
            keyDownHandler:
              | ((event: {
                  keyCode?: number;
                  preventDefaultAction?: boolean;
                  originalEvent?: { key?: string; keyCode?: number };
                }) => unknown)
              | null;
          };
        }
      ).innerTracker;
      if (keyTracker) {
        const originalKeyDown = keyTracker.keyDownHandler;
        keyTracker.keyDownHandler = function (event): unknown {
          const nav = onArrowNavigateRef.current;
          if (nav) {
            const oe = event.originalEvent;
            const key = oe?.key;
            const code = event.keyCode ?? oe?.keyCode;
            const isPrev =
              key === "ArrowUp" ||
              key === "ArrowLeft" ||
              code === 38 ||
              code === 37;
            const isNext =
              key === "ArrowDown" ||
              key === "ArrowRight" ||
              code === 40 ||
              code === 39;
            if (isPrev || isNext) {
              event.preventDefaultAction = true;
              nav(isPrev ? "previous" : "next");
              return false;
            }
          }
          return typeof originalKeyDown === "function"
            ? originalKeyDown.call(this, event)
            : undefined;
        };
      }
    } catch {
      // jsdom / stub paths: ignore.
    }

    const onOpenFailed = (e: unknown) => {
      const m =
        (e as { message?: string } | null | undefined)?.message ?? "unknown";
      // Ignore a SUPERSEDED failure: the progressive upgrade opens the full-res
      // source over the still-loading thumb900 primary, which aborts the
      // primary and makes OSD fire `open-failed` for it. OSD's message embeds
      // the failed source URL ("…image at <url>"); if it isn't the URL we most
      // recently asked to open, this is that expected abort, not a real error —
      // the current source is still loading/loaded, so don't surface it.
      const latest = latestOpenUrlRef.current;
      if (
        latest &&
        m !== "unknown" &&
        m.includes("http") &&
        !m.includes(latest)
      )
        return;
      // If a fallback URL is configured and we haven't already tried it,
      // swap the tile source and retry. This covers the local-dev case
      // where `urls.full` returns a placeholder PNG OSD can't decode at
      // its expected dims; the analysis page passes `urls.thumb900` as
      // the fallback so the user sees real content.
      const fb = fallbackImageUrlRef.current;
      if (fb && !fallbackTriedRef.current && fb !== imageUrlRef.current) {
        fallbackTriedRef.current = true;
        try {
          latestOpenUrlRef.current = fb;
          viewer.open({ type: "image", url: fb });
          return;
        } catch {
          // fall through to surfaced error
        }
      }
      // Don't flash the error overlay for a transient failure: schedule it, and
      // let a subsequent successful open (e.g. the full-res progressive swap
      // landing a beat later) cancel it via `dismissError`. Only a failure that
      // nothing recovers from within the grace window reaches the user.
      if (errorTimerRef.current !== null) clearTimeout(errorTimerRef.current);
      errorTimerRef.current = window.setTimeout(() => {
        errorTimerRef.current = null;
        setErrorMsg(m);
      }, ERROR_SURFACE_GRACE_MS);
    };
    viewer.addHandler("open-failed", onOpenFailed);

    // Story 012 — apply caller-provided initial viewport once on first
    // open, then publish every viewport change to the host so per-image
    // zoom can be persisted client-side. Caught + ignored in jsdom where
    // OSD's viewport math has no real DOM rect to work with. Both
    // callbacks read from refs so callback-identity churn doesn't tear
    // OSD down between renders.
    const onOpen = () => {
      // Any successful open cancels a pending/shown error overlay — including
      // the transient one a thumb900 abort or contention failure leaves before
      // the full-res source finishes loading.
      dismissErrorRef.current();
      const iv = initialViewportRef.current;
      if (!iv) return;
      try {
        viewer.viewport.zoomTo(iv.zoom, undefined, true);
        viewer.viewport.panTo(
          new OpenSeadragon.Point(iv.centerX, iv.centerY),
          true,
        );
      } catch {
        // jsdom: ignore.
      }
    };
    viewer.addHandler("open", onOpen);

    const onViewport = () => {
      try {
        const center = viewer.viewport.getCenter();
        const zoom = viewer.viewport.getZoom();
        const cb = onViewportChangeRef.current;
        if (cb) {
          cb({
            zoom,
            centerX: center.x,
            centerY: center.y,
          });
        }
        // R58-L-15 AC5 — publish zoom % (relative to fit-to-viewport).
        // OSD's `getZoom()` returns the multiplier of fit-zoom (1.0 = fit
        // because `defaultZoomLevel: 0` + `homeFillsViewer: false`).
        const pctCb = onZoomPercentChangeRef.current;
        if (pctCb) {
          const pct = Math.max(100, Math.round(zoom * 100));
          pctCb(pct);
        }
      } catch {
        // jsdom: ignore.
      }
    };
    viewer.addHandler("viewport-change", onViewport);

    // Re-tick OSD whenever the container resizes — RGL mounts cells at
    // width/height 0 and resolves real dimensions on the next frame,
    // and OSD's auto-resize handling sometimes misses that initial
    // 0→N transition. Forcing `world.update()` on every observed resize
    // keeps the canvas in sync with the actual element size.
    const resizeObserver =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            try {
              viewer.viewport.resize();
              viewer.viewport.applyConstraints();
            } catch {
              // jsdom: ignore
            }
          })
        : null;
    if (resizeObserver) resizeObserver.observe(node);

    return () => {
      if (errorTimerRef.current !== null) {
        clearTimeout(errorTimerRef.current);
        errorTimerRef.current = null;
      }
      try {
        if (resizeObserver) resizeObserver.disconnect();
        viewer.removeHandler("open-failed", onOpenFailed);
        viewer.removeHandler("open", onOpen);
        viewer.removeHandler("viewport-change", onViewport);
        viewer.destroy();
      } catch {
        // jsdom paths may throw on destroy; harmless in tests.
      }
      viewerRef.current = null;
    };
    // Mount once: the viewer is created a single time. URL changes are applied
    // by the sync effect below (swap-in-place), never by rebuilding here — so
    // this effect intentionally has no dependencies.
  }, []);

  // Swap OSD's tile source IN PLACE when the URL props change — the old code
  // rebuilt the whole viewer for this. The primary (thumb900) opens
  // immediately; the higher-res `progressiveImageUrl` is preloaded and swapped
  // in on decode, exactly as before, but against the already-mounted viewer so
  // navigation no longer destroys/recreates the canvas + GL context.
  useEffect(() => {
    imageUrlRef.current = imageUrl;
    fallbackImageUrlRef.current = fallbackImageUrl;
    const viewer = viewerRef.current;
    if (!viewer) return;

    // Open the new primary only when it actually changed. The first run matches
    // the constructor's tileSources (openedPrimaryRef seeded with the mount
    // imageUrl), so it's a no-op — no redundant re-open on mount. A genuinely
    // new image clears any stale error and gets a fresh fallback attempt.
    if (imageUrl !== openedPrimaryRef.current) {
      openedPrimaryRef.current = imageUrl;
      fallbackTriedRef.current = false;
      dismissErrorRef.current();
      try {
        latestOpenUrlRef.current = imageUrl;
        viewer.open({ type: "image", url: imageUrl });
      } catch {
        // jsdom stub has no `open()`; harmless in tests.
      }
    }

    // Progressive upgrade: kick off a hidden <img> preload of the higher-res
    // URL; on `load`, decode then swap OSD's tile source so the user sees
    // full-res pixels without a blank window during the JPEG decode. Skipped
    // if there's no progressive URL or it equals the primary. The cleanup
    // detaches this preload's handlers AND flips `cancelled`, so a late
    // decode after the user has navigated on can never swap a stale image
    // into the viewer.
    let progressivePreload: HTMLImageElement | null = null;
    // PERF-09 (audit I-10) — once `decode()` is in flight, detaching `onload`
    // alone can no longer stop the swap (the promise continuation outlives
    // it), so this flag is re-checked right before `open()`.
    let cancelled = false;
    if (progressiveImageUrl && progressiveImageUrl !== imageUrl) {
      progressivePreload = new Image();
      progressivePreload.crossOrigin = "anonymous";
      const preload = progressivePreload;
      progressivePreload.onload = () => {
        // PERF-09 — rasterise the JPEG off the main thread BEFORE handing
        // the URL to OSD, so the in-place `open()` swap never jank-decodes
        // on the main thread (Chromium's decoded-image cache makes OSD's own
        // re-decode of the same URL cheap). `decode()` may be missing
        // (jsdom) or reject (decode error / discarded image) — both fall
        // back to "treat as decoded" and swap exactly as before.
        const decoded =
          typeof preload.decode === "function"
            ? preload.decode()
            : Promise.resolve();
        void decoded
          .catch(() => undefined)
          .then(() => {
            if (cancelled || !viewerRef.current) return;
            try {
              // `open()` swaps the source in place — OSD preserves the current
              // viewport (zoom + pan) when the new tile source resolves. The user
              // sees a brief soft fade then full-res. This aborts the primary's
              // in-flight load; `latestOpenUrlRef` makes onOpenFailed treat that
              // abort as superseded rather than an error.
              latestOpenUrlRef.current = progressiveImageUrl;
              viewer.open({ type: "image", url: progressiveImageUrl });
            } catch {
              // Real-DOM paths may throw if the canvas was unmounted between
              // preload start and finish — safe to ignore.
            }
          });
      };
      progressivePreload.onerror = () => {
        // Full-res unavailable; we already showed thumb_900, so just stay
        // there. No user-visible error.
      };
      progressivePreload.src = progressiveImageUrl;
    }

    return () => {
      cancelled = true;
      if (progressivePreload) {
        progressivePreload.onload = null;
        progressivePreload.onerror = null;
        progressivePreload.src = "";
        progressivePreload = null;
      }
    };
  }, [imageUrl, fallbackImageUrl, progressiveImageUrl]);

  // R58-L-17 — keep OSD's `maxZoomLevel` in sync with the user setting.
  // The DefinitelyTyped declarations don't expose `maxZoomLevel` on the
  // Viewport instance, but the OSD runtime does read it directly off
  // the viewport (it's set from the constructor option of the same
  // name). We mutate it through a narrow cast and re-apply constraints
  // so an in-flight zoom past the new cap snaps back.
  useEffect(() => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    try {
      (viewer.viewport as unknown as { maxZoomLevel: number }).maxZoomLevel =
        Math.max(1, maxZoomPercent / 100);
      viewer.viewport.applyConstraints();
    } catch {
      // jsdom: ignore.
    }
  }, [maxZoomPercent]);

  // Track wrapper size + bounding-box screen projection on every viewport change.
  useEffect(() => {
    const node = containerRef.current;
    const viewer = viewerRef.current;
    if (!node || !viewer) return;

    const recompute = () => {
      const rect = node.getBoundingClientRect();
      setSize({ width: rect.width, height: rect.height });

      const projected = boundingBoxes.map<ScreenBox>((b) => {
        try {
          const tl = viewer.viewport.imageToViewerElementCoordinates(
            new OpenSeadragon.Point(b.x, b.y),
          );
          const br = viewer.viewport.imageToViewerElementCoordinates(
            new OpenSeadragon.Point(b.x + b.width, b.y + b.height),
          );
          return {
            ...b,
            screenX: tl.x,
            screenY: tl.y,
            screenWidth: br.x - tl.x,
            screenHeight: br.y - tl.y,
          };
        } catch {
          return {
            ...b,
            screenX: 0,
            screenY: 0,
            screenWidth: 0,
            screenHeight: 0,
          };
        }
      });
      setScreenBoxes(projected);

      // ANA-R3-GEOM-03 — project polygon overlays through the SAME OSD
      // viewport transform. Paths are normalised 0..1; map each vertex to a
      // viewer-element CSS px point. A degenerate/empty path projects to [].
      const projectedPolys = polygonOverlays.map<ScreenPolygon>((poly) => {
        const flat: number[] = [];
        try {
          for (let i = 0; i + 1 < poly.path.length; i += 2) {
            const nx = poly.path[i];
            const ny = poly.path[i + 1];
            if (typeof nx !== "number" || typeof ny !== "number") continue;
            const sp = viewer.viewport.imageToViewerElementCoordinates(
              // Normalised image coords map onto OSD's image-pixel space by
              // scaling with the content size; but OSD's
              // imageToViewerElementCoordinates expects IMAGE-PIXEL coords.
              // We convert normalised → image px via the loaded content size.
              normalisedToImagePoint(viewer, nx, ny),
            );
            flat.push(sp.x, sp.y);
          }
        } catch {
          // jsdom / pre-open: leave whatever projected so far (usually []).
        }
        const closed = poly.closed ?? isClosedFlat(poly.path);
        return {
          id: poly.id,
          screenFlat: flat,
          closed,
          color: poly.color,
          label: poly.label,
          style: poly.style,
        };
      });
      setScreenPolygons(projectedPolys);

      // ANA-R3-GEOM-03 — publish the live viewport projection so a draw overlay
      // can build a CoordinateTransform. Bump the version every recompute (any
      // OSD open / viewport-change / resize) so the overlay re-projects.
      const apiCb = onViewportApiReadyRef.current;
      if (apiCb) {
        viewportVersionRef.current += 1;
        apiCb(makeViewportApi(viewer, viewportVersionRef.current));
      }
    };

    // ANA-R2-01 — the load-time render race. PREVIOUSLY this effect only
    // projected `boundingBoxes` → `screenBoxes` from inside OSD event
    // handlers (`open`/`viewport-change`/`resize`). That left two gaps that
    // make the overlay disagree with the List/Tiles until a stray event
    // (e.g. a list-selection pan) forces a re-projection:
    //
    //   1. ASYNC FINDINGS LAND AFTER `open`. On arrow nav the new image's
    //      `open` fires first; the per-image findings query resolves a beat
    //      later and changes the `boundingBoxes` prop. This effect re-runs
    //      and re-registers the handlers, but real OSD does NOT re-fire
    //      `open` just because a handler was added, and no viewport change
    //      happens — so `recompute()` never ran and the overlay stayed empty
    //      (Christian [00:14:13]: boxes only appear "as i click it"). The
    //      unit test stub fires `open` on every addHandler, which is exactly
    //      why R1-02's tests missed this real-OSD-only race.
    //   2. THUMBNAIL→FULL-RES SWAP. `viewer.open()` for the progressive
    //      upgrade tears down + rebuilds OSD's `world`; the brief window with
    //      an empty world (or a fresh `open`) could leave the Konva overlay
    //      projecting against stale/empty geometry. Christian's hypothesis
    //      [00:24:09]: "the full resolution image becomes available and this
    //      hides the bounding boxes."
    //
    // FIX: project SYNCHRONOUSLY whenever this effect runs (i.e. on every
    // `boundingBoxes` change), independent of OSD events — so the overlay is
    // redrawn the instant the effective finding set changes, no selection
    // required. We also subscribe to `fully-loaded` so the overlay is
    // re-projected after the full-res tile source settles post-swap. The
    // world may still be empty when this first runs (pre-`open`); the
    // try/catch in `recompute` degrades each box to a 0-size projection,
    // and the subsequent `open`/`viewport-change` reprojects with real
    // geometry — so a premature call is harmless.
    recompute();

    viewer.addHandler("open", recompute);
    viewer.addHandler("viewport-change", recompute);
    viewer.addHandler("resize", recompute);
    // ANA-R2-01 — re-project after the thumbnail→full-res tile swap PAINTS.
    // `tile-drawn` fires as the new tile source's tiles are drawn to the OSD
    // canvas (it fires on the progressive `viewer.open()` swap too), so the
    // Konva overlay is re-projected on top of the freshly-painted full-res
    // image — covering Christian's hypothesis [00:24:09] that the full-res
    // image "hides the bounding boxes". (OSD's `fully-loaded` runtime event
    // isn't in the typed event map; `tile-drawn` is, fires sooner, and is the
    // event AC1 names.)
    viewer.addHandler("tile-drawn", recompute);

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(recompute)
        : null;
    ro?.observe(node);

    return () => {
      viewer.removeHandler("open", recompute);
      viewer.removeHandler("viewport-change", recompute);
      viewer.removeHandler("resize", recompute);
      viewer.removeHandler("tile-drawn", recompute);
      ro?.disconnect();
      // ANA-R3-GEOM-03 — tear down the published viewport API on unmount.
      onViewportApiReadyRef.current?.(null);
    };
  }, [boundingBoxes, polygonOverlays]);

  // Story 032 — right-click drag-to-zoom. Wires DOM mouse events on the
  // outer wrapper; OSD's own MouseTracker keeps left-click pan + wheel
  // zoom behaviour intact. The marquee is a plain absolutely-positioned
  // <Box> on top of OSD (no Konva needed for a single rectangle, and
  // it keeps the gesture working even when there are zero bboxes).
  //
  // Lifecycle:
  //   * mousedown (button 2) → record start point + reset marquee
  //   * mousemove (while active) → update marquee width/height
  //   * mouseup → commit zoom if marquee >= MIN px on each axis, else no-op
  //   * keydown Escape → cancel
  //   * contextmenu → preventDefault so the browser menu doesn't fight us
  // Right-click marquee state lives in refs so the JSX-bound handlers
  // (below) can read + mutate without `setState` race conditions during
  // the same gesture. `setMarquee` still drives the React-managed
  // rectangle div so it actually paints. Pre-Story 050 these handlers
  // were attached inside `useEffect` against `wrapperRef.current`; that
  // works in production but leaves a one-tick race with Playwright when
  // the test fires mousedown before React commits its first effect,
  // which is exactly what 032 AC4 + AC1 hit in the new (heavier) chrome
  // under parallel workers. Moving the wrapper-scoped listeners to JSX
  // props makes them part of the initial render — they're live the
  // moment the Viewer's wrapper Box is in the DOM.
  const dragStartRef = useRef<{ x: number; y: number } | null>(null);
  const activeRef = useRef<boolean>(false);

  const cancelMarquee = useCallback((): void => {
    dragStartRef.current = null;
    activeRef.current = false;
    setMarquee(null);
  }, []);

  const handleContextMenu = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      // Suppress the native menu inside the viewer so right-click is
      // unambiguously the zoom gesture.
      e.preventDefault();
      // R58-L-16 AC4 — double-right-click → fit-to-viewport reset.
      // `contextmenu` fires on every right-mouse-down, so two such
      // events within DBL_RCLICK_WINDOW_MS count as a "double".
      const now =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      if (now - lastContextMenuRef.current < DBL_RCLICK_WINDOW_MS) {
        const viewer = viewerRef.current;
        if (viewer) {
          try {
            viewer.viewport.goHome(false);
          } catch {
            // jsdom: ignore.
          }
        }
        // Cancel any in-flight marquee so the gesture doesn't leak a zoom.
        cancelMarquee();
        lastContextMenuRef.current = 0;
        return;
      }
      lastContextMenuRef.current = now;
    },
    [cancelMarquee],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      // R58-L-16 — middle-mouse drag pan (AC6) takes precedence over the
      // right-button marquee. We track the cursor in CSS-px and call
      // OSD's `panBy` with the *viewport-coord* delta on every move.
      if (e.button === 1) {
        e.preventDefault();
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        const rect = wrapper.getBoundingClientRect();
        middlePanRef.current = {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        };
        setPanActive(true);
        return;
      }
      // R58-L-16 — Space-held + left-drag pan (AC5). Same coord
      // tracking as middle-mouse; cursor reads "grabbing" while active.
      if (e.button === 0 && spaceDownRef.current) {
        e.preventDefault();
        const wrapper = wrapperRef.current;
        if (!wrapper) return;
        const rect = wrapper.getBoundingClientRect();
        spacePanRef.current = {
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        };
        return;
      }
      if (e.button !== 2) return;
      e.preventDefault();
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      dragStartRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
      activeRef.current = true;
      setMarquee({
        x: dragStartRef.current.x,
        y: dragStartRef.current.y,
        width: 0,
        height: 0,
      });
    },
    [],
  );

  // R58-L-16 — mouse-wheel zoom. We register a NON-PASSIVE native
  // listener via the effect below; React's `onWheel` prop registers as
  // passive in modern Chrome, which makes `e.preventDefault()` silently
  // ignored. With a non-passive native listener the prevent lands and
  // the page doesn't scroll while the user is zooming inside the
  // viewer.
  //
  // Q9 (2026-05-23) — OSD smooth integration with 1.25× per-100-px
  // factor. All math lives in `wheelZoomMath.ts` so the curves are
  // unit-testable without OpenSeadragon's viewport (jsdom-blind). One
  // canonical mouse-wheel notch (deltaY = ±100, deltaMode 0) zooms by
  // exactly 1.25× (in) or 1/1.25× (out); trackpads emitting many small
  // deltas integrate smoothly via the exponent.
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const onWheel = (e: WheelEvent): void => {
      // AC11 — Ctrl + wheel passes through to the browser's page-zoom.
      // metaKey too (macOS Cmd+wheel) for parity with the platform's
      // page-zoom shortcut.
      if (e.ctrlKey || e.metaKey) return;
      e.preventDefault();
      const viewer = viewerRef.current;
      if (!viewer) return;
      const rect = wrapper.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      try {
        const refPoint = viewer.viewport.viewerElementToViewportCoordinates(
          new OpenSeadragon.Point(cx, cy),
        );
        const current = viewer.viewport.getZoom();
        // SB6-ANA-POP-01 — floor wheel-out at the contain-fit zoom (what
        // `goHome` reaches), not the absolute `1.0` width-fill. `getMinZoom()`
        // now returns `getHomeZoom()` (see `minZoomImageRatio: 1` above), and
        // recomputes on window/viewport resize so a popped-out window that's
        // dragged to another display still bottoms out at "whole image visible".
        const minZ = viewer.viewport.getMinZoom();
        const maxZ = Math.max(1, maxZoomPercentRef.current / 100);
        const deltaMode: 0 | 1 | 2 =
          e.deltaMode === 1 || e.deltaMode === 2 ? e.deltaMode : 0;
        const effFactor = effectiveWheelZoomFactor({
          deltaY: e.deltaY,
          deltaMode,
          currentZoom: current,
          minZoom: minZ,
          maxZoom: maxZ,
        });
        if (effFactor === 1) return;
        // Cursor-centring (AC8 contract): zoomBy with a reference point
        // in viewport coords keeps that point under the cursor after the
        // zoom is applied. `false` for immediate (no animation) so the
        // wheel feels responsive even on slow machines.
        viewer.viewport.zoomBy(effFactor, refPoint, false);
        viewer.viewport.applyConstraints();
      } catch {
        // jsdom: ignore.
      }
    };
    wrapper.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      wrapper.removeEventListener("wheel", onWheel);
    };
  }, []);

  // R58-L-15 — cursor pixel coords. Fires the host callback on every
  // mouse move; null on mouse leave. The transform from CSS-px → image
  // pixels is OSD's `viewerElementToImageCoordinates`. Rounded so the
  // readout doesn't strobe between adjacent integers under sub-pixel
  // motion.
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLDivElement>): void => {
      // R58-L-16 — middle-mouse pan path.
      if (middlePanRef.current) {
        const wrapper = wrapperRef.current;
        const viewer = viewerRef.current;
        if (wrapper && viewer) {
          const rect = wrapper.getBoundingClientRect();
          const cx = e.clientX - rect.left;
          const cy = e.clientY - rect.top;
          const dx = cx - middlePanRef.current.x;
          const dy = cy - middlePanRef.current.y;
          middlePanRef.current = { x: cx, y: cy };
          try {
            // Convert pixel delta → viewport-coord delta. Two-point
            // diff in viewer-element space → two-point diff in
            // viewport space → call panBy(-delta) to follow cursor.
            const a = viewer.viewport.viewerElementToViewportCoordinates(
              new OpenSeadragon.Point(0, 0),
            );
            const b = viewer.viewport.viewerElementToViewportCoordinates(
              new OpenSeadragon.Point(dx, dy),
            );
            viewer.viewport.panBy(
              new OpenSeadragon.Point(a.x - b.x, a.y - b.y),
              false,
            );
            viewer.viewport.applyConstraints();
          } catch {
            // jsdom: ignore.
          }
        }
      }
      // R58-L-16 — Space-held + left-drag pan.
      if (spacePanRef.current) {
        const wrapper = wrapperRef.current;
        const viewer = viewerRef.current;
        if (wrapper && viewer) {
          const rect = wrapper.getBoundingClientRect();
          const cx = e.clientX - rect.left;
          const cy = e.clientY - rect.top;
          const dx = cx - spacePanRef.current.x;
          const dy = cy - spacePanRef.current.y;
          spacePanRef.current = { x: cx, y: cy };
          try {
            const a = viewer.viewport.viewerElementToViewportCoordinates(
              new OpenSeadragon.Point(0, 0),
            );
            const b = viewer.viewport.viewerElementToViewportCoordinates(
              new OpenSeadragon.Point(dx, dy),
            );
            viewer.viewport.panBy(
              new OpenSeadragon.Point(a.x - b.x, a.y - b.y),
              false,
            );
            viewer.viewport.applyConstraints();
          } catch {
            // jsdom: ignore.
          }
        }
      }
      const cbPixel = onCursorImagePixelRef.current;
      const cbNorm = onCursorImageNormalizedRef.current;
      if (!cbPixel && !cbNorm) return;
      const viewer = viewerRef.current;
      const wrapper = wrapperRef.current;
      if (!viewer || !wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      try {
        const p = viewer.viewport.viewerElementToImageCoordinates(
          new OpenSeadragon.Point(cx, cy),
        );
        if (cbPixel) cbPixel({ u: Math.round(p.x), v: Math.round(p.y) });
        if (cbNorm) {
          // Story 032 — normalise by the *loaded* image's content size so the
          // fraction is identical whether the thumbnail (≈900 px) or the
          // full-res image (≈8192 px) is currently mounted. pic2comp only uses
          // u/W and v/H, so this keeps the radius readout stable across the
          // progressive upgrade.
          // getItemAt is typed non-null but returns undefined while the world
          // is still loading — gate on the item count instead of a truthiness
          // check (which the type system flags as always-true).
          if (viewer.world.getItemCount() > 0) {
            const size = viewer.world.getItemAt(0).getContentSize();
            if (size.x > 0 && size.y > 0) {
              cbNorm({ nx: p.x / size.x, ny: p.y / size.y });
            }
          }
        }
      } catch {
        // jsdom: ignore.
      }
    },
    [],
  );

  // R58-L-15 AC2 — cursor leaves the wrapper → publish null so the
  // radius readout disappears.
  const handleMouseLeave = useCallback((): void => {
    const cb = onCursorImagePixelRef.current;
    if (cb) cb(null);
    const cbNorm = onCursorImageNormalizedRef.current;
    if (cbNorm) cbNorm(null);
  }, []);

  // Window-level handlers stay in useEffect — they fire on bubbled
  // events outside the wrapper (e.g. mouseup after dragging off-canvas)
  // which can't be expressed as JSX props.
  useEffect(() => {
    const onMouseMove = (e: MouseEvent): void => {
      if (!activeRef.current || !dragStartRef.current) return;
      const wrapper = wrapperRef.current;
      if (!wrapper) return;
      const rect = wrapper.getBoundingClientRect();
      const cx = e.clientX - rect.left;
      const cy = e.clientY - rect.top;
      const start = dragStartRef.current;
      const x = Math.min(start.x, cx);
      const y = Math.min(start.y, cy);
      const width = Math.abs(cx - start.x);
      const height = Math.abs(cy - start.y);
      setMarquee({ x, y, width, height });
    };

    const onMouseUp = (e: MouseEvent): void => {
      if (e.button !== 2 || !activeRef.current || !dragStartRef.current) {
        return;
      }
      const viewer = viewerRef.current;
      const wrapper = wrapperRef.current;
      if (!wrapper) {
        cancelMarquee();
        return;
      }
      const wrapperRect = wrapper.getBoundingClientRect();
      // SB6-ANA-VIEW-01 / Q3 — when the cursor leaves the canvas mid-
      // drag, Christian's spec is to commit the zoom at the LAST valid
      // in-canvas mouse position (treat the leave as a synthetic
      // right-click). We clamp the raw cursor coords to the wrapper
      // rect; the marquee state already mirrors the in-bounds position
      // because `onMouseMove` reads the same clamping behaviour from
      // the `getBoundingClientRect` math the move handler uses.
      const rawX = e.clientX - wrapperRect.left;
      const rawY = e.clientY - wrapperRect.top;
      const cx = Math.max(0, Math.min(wrapperRect.width, rawX));
      const cy = Math.max(0, Math.min(wrapperRect.height, rawY));
      const start = dragStartRef.current;
      const x0 = Math.min(start.x, cx);
      const y0 = Math.min(start.y, cy);
      const x1 = Math.max(start.x, cx);
      const y1 = Math.max(start.y, cy);
      const width = x1 - x0;
      const height = y1 - y0;
      cancelMarquee();
      // SB6-ANA-VIEW-01-FIX — commit the zoom for any intentional drag.
      // A near-zero click is left untouched (R58-L-16 AC3); a small box
      // is NOT rejected — `fitBoundsWithConstraints` below clamps the
      // resulting zoom to `maxZoomPercent`, so a tiny box lands exactly
      // at max-zoom (R58-L-16 AC2 + the intent of OQ-SB6-C6).
      if (!marqueeIsDragGesture(width, height)) return;
      if (!viewer) return;
      try {
        const tl = viewer.viewport.viewerElementToImageCoordinates(
          new OpenSeadragon.Point(x0, y0),
        );
        const br = viewer.viewport.viewerElementToImageCoordinates(
          new OpenSeadragon.Point(x1, y1),
        );
        const imageRect = viewer.viewport.imageToViewportRectangle(
          new OpenSeadragon.Rect(tl.x, tl.y, br.x - tl.x, br.y - tl.y),
        );
        viewer.viewport.fitBoundsWithConstraints(imageRect, false);
      } catch {
        // jsdom or pre-open Viewer — ignore.
      }
    };

    const onKeyDown = (e: KeyboardEvent): void => {
      if (e.key === "Escape" && activeRef.current) {
        e.preventDefault();
        cancelMarquee();
      }
      // R58-L-16 — Space tracking for pan (AC5). We don't preventDefault
      // unless the user is interacting with the viewer (i.e. the wrapper
      // is focus-within); space-as-page-scroll outside the viewer is
      // still the browser's job.
      if (e.code === "Space" && !e.repeat) {
        const wrapper = wrapperRef.current;
        if (
          wrapper &&
          (wrapper.contains(document.activeElement) ||
            wrapper === document.activeElement ||
            wrapper.matches(":hover"))
        ) {
          e.preventDefault();
          spaceDownRef.current = true;
          setPanActive(true);
        }
      }
    };

    const onKeyUp = (e: KeyboardEvent): void => {
      if (e.code === "Space") {
        spaceDownRef.current = false;
        spacePanRef.current = null;
        if (!middlePanRef.current) setPanActive(false);
      }
    };

    // R58-L-16 — release middle-mouse + space-pan on any mouseup. Same
    // window-level wiring as the marquee mouseup so a drag-off-canvas
    // release still ends the gesture cleanly.
    const onPanMouseUp = (e: MouseEvent): void => {
      if (e.button === 1) {
        middlePanRef.current = null;
        if (!spaceDownRef.current) setPanActive(false);
      }
      if (e.button === 0) spacePanRef.current = null;
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
    window.addEventListener("mouseup", onPanMouseUp);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("mouseup", onPanMouseUp);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, [cancelMarquee]);

  // SETTINGS-02 — dense-declutter. Count boxes whose projected rect
  // overlaps the viewport; when that exceeds the threshold (and the
  // caller enabled it) hide every label until the analyst zooms in
  // enough to drop below it. Recomputes whenever screenBoxes change
  // (i.e. on every viewport-change), so labels reappear on zoom-in.
  const boxesInViewport = screenBoxes.reduce((n, b) => {
    const intersects =
      b.screenX + b.screenWidth >= 0 &&
      b.screenX <= size.width &&
      b.screenY + b.screenHeight >= 0 &&
      b.screenY <= size.height;
    return intersects ? n + 1 : n;
  }, 0);
  const labelsAutoHidden =
    autoHideLabelsWhenDense && boxesInViewport > DENSE_LABEL_VIEWPORT_THRESHOLD;

  return (
    <Box
      ref={wrapperRef}
      role="region"
      aria-label={ariaLabel}
      tabIndex={0}
      onContextMenu={handleContextMenu}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      data-testid="viewer-wrapper"
      data-max-zoom-percent={String(maxZoomPercent)}
      data-pan-active={panActive ? "true" : "false"}
      data-marquee-active={marquee ? "true" : "false"}
      sx={{
        // Fill the parent panel cell instead of insisting on a 480-px
        // floor — RGL gives the Viewer cell a fixed pixel height
        // (rowHeight × h - margins) that's often shorter than 480, and
        // the old `minHeight: 480` made the panel overflow + scroll
        // *while OSD initialised with the still-zero container width*,
        // so the canvas drew at 0×0 and the user saw an empty Viewer
        // with the DrawToolbar floating on top.
        position: "relative",
        width: "100%",
        height: "100%",
        minHeight: 240,
        bgcolor: "background.paper",
        // Drop the visible border/radius — the outer Workspace panel
        // already supplies them. Doubling produced a thin rectangle
        // around the canvas that read as a "frame".
        overflow: "hidden",
        outline: "none",
        // R58-L-16 — cursor flips to "grabbing" while panning. Space-
        // down OR middle-mouse drag-active both qualify.
        '&[data-pan-active="true"]': { cursor: "grabbing" },
        // SB6-ANA-VIEW-01 — crosshair while the user is drawing the
        // right-mouse zoom marquee, so the affordance reads as
        // "you're framing a zoom".
        '&[data-marquee-active="true"]': { cursor: "crosshair" },
        "&:focus-visible": {
          outline: 2,
          outlineColor: "primary.main",
          outlineOffset: 2,
        },
      }}
    >
      <Box
        id={containerId}
        ref={containerRef}
        aria-hidden="true"
        sx={{ position: "absolute", inset: 0, width: "100%", height: "100%" }}
      />

      {errorMsg ? (
        <Box
          role="alert"
          aria-live="assertive"
          aria-atomic="true"
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "background.paper",
          }}
        >
          <Typography color="text.secondary">
            Couldn&apos;t load image: {errorMsg}
          </Typography>
        </Box>
      ) : null}

      {size.width > 0 &&
      size.height > 0 &&
      (screenBoxes.length > 0 || screenPolygons.length > 0) ? (
        <Box
          aria-hidden="true"
          sx={{ position: "absolute", inset: 0, pointerEvents: "none" }}
          data-testid="viewer-overlay"
          // ANA-R2-01 — the Konva Stage renders to a <canvas> that Playwright
          // can't pixel-inspect, so the actually-PROJECTED box count is
          // mirrored here. Unlike the `viewer-ai-bbox-ids` seam in the
          // analysis feature (which mirrors the React PROPS), this reflects
          // `screenBoxes` — the boxes that have been projected through OSD's
          // viewport transform and are genuinely drawn. The GT 48 e2e asserts
          // on THIS so it exercises the real render lifecycle, not the props.
          data-box-count={String(screenBoxes.length)}
          data-polygon-count={String(screenPolygons.length)}
        >
          <Stage
            width={size.width}
            height={size.height}
            listening={!!onBoxClick}
          >
            <Layer>
              {/* ANA-R3-GEOM-03 — free-form outlines. A closed ring renders as
                  a filled <Line>; an open polyline strokes only. Selection uses
                  the same accent + thicker stroke as boxes. Drawn BEFORE boxes
                  so a same-id box (there shouldn't be one — a defect is in
                  exactly one list) wouldn't be hidden. Non-listening: polygon
                  hit-testing/selection still flows through the bbox <Rect> that
                  the caller may render alongside, keeping AC4 box interaction. */}
              {screenPolygons.map((poly) => {
                if (poly.screenFlat.length < 4) return null;
                const isSelected = poly.id === selectedBoxId;
                const st = poly.style;
                const strokeColor = isSelected
                  ? (st?.activeColor ?? SELECTED_BOX_COLOR)
                  : (st?.color ?? poly.color ?? DEFAULT_BOX_COLOR);
                const strokeWidth = isSelected
                  ? (st?.activeStrokeWidth ?? SELECTED_BOX_STROKE_WIDTH)
                  : (st?.strokeWidth ?? DEFAULT_BOX_STROKE_WIDTH);
                let fill: string | undefined;
                if (poly.closed) {
                  if (st) {
                    const op = isSelected
                      ? (st.activeFillOpacity ?? 0)
                      : (st.fillOpacity ?? 0);
                    fill = op > 0 ? hexToRgba(strokeColor, op) : undefined;
                  } else {
                    fill = isSelected ? SELECTED_BOX_FILL : undefined;
                  }
                }
                const outlineProps = st?.outline
                  ? {
                      shadowColor: CANVAS_SCRIM,
                      shadowBlur: 2,
                      shadowOpacity: 0.85,
                      shadowOffset: { x: 0, y: 0 },
                    }
                  : {};
                return (
                  <Line
                    key={poly.id}
                    points={poly.screenFlat}
                    closed={poly.closed}
                    stroke={strokeColor}
                    strokeWidth={strokeWidth}
                    {...(fill ? { fill } : {})}
                    {...outlineProps}
                    listening={!!onBoxClick}
                    {...(onBoxClick
                      ? {
                          onClick: () => {
                            onBoxClick(poly.id);
                          },
                        }
                      : {})}
                  />
                );
              })}
              {/*
                R58-L-13 AC8 — overlapping-bbox click resolution.
                Konva's hit-test for a click event walks the layer
                children in REVERSE draw order and returns the FIRST
                listening shape under the cursor. We render screenBoxes
                in their array order, so the LAST element of the array
                is drawn on top AND wins the click. Callers that need
                deterministic top-resolution should ensure the array's
                tail holds the visually-topmost bbox (the analysis
                feature sorts defects BEFORE findings, so AI bboxes win
                in overlap regions when both kinds collide).
              */}
              {screenBoxes.map((b) => {
                const isSelected = b.id === selectedBoxId;
                const st = b.style;
                // Stroke colour + width: per-box style with fallback to
                // the legacy constants so style-less callers are unchanged.
                const strokeColor = isSelected
                  ? (st?.activeColor ?? SELECTED_BOX_COLOR)
                  : (st?.color ?? b.color ?? DEFAULT_BOX_COLOR);
                const strokeWidth = isSelected
                  ? (st?.activeStrokeWidth ?? SELECTED_BOX_STROKE_WIDTH)
                  : (st?.strokeWidth ?? DEFAULT_BOX_STROKE_WIDTH);
                // Fill: from style opacity when provided; else preserve the
                // legacy selected-only translucent fill.
                let fill: string | undefined;
                if (st) {
                  const op = isSelected
                    ? (st.activeFillOpacity ?? 0)
                    : (st.fillOpacity ?? 0);
                  fill = op > 0 ? hexToRgba(strokeColor, op) : undefined;
                } else {
                  fill = isSelected ? SELECTED_BOX_FILL : undefined;
                }
                // Contrasting outline → a dark halo via Konva shadow so the
                // box reads on both bright blade + dark sky (no extra node).
                const outlineProps = st?.outline
                  ? {
                      shadowColor: CANVAS_SCRIM,
                      shadowBlur: 2,
                      shadowOpacity: 0.85,
                      shadowOffset: { x: 0, y: 0 },
                    }
                  : {};
                return (
                  <Rect
                    key={b.id}
                    x={b.screenX}
                    y={b.screenY}
                    width={b.screenWidth}
                    height={b.screenHeight}
                    stroke={strokeColor}
                    strokeWidth={strokeWidth}
                    {...(fill ? { fill } : {})}
                    {...outlineProps}
                    listening={!!onBoxClick}
                    {...(onBoxClick
                      ? {
                          onClick: () => {
                            onBoxClick(b.id);
                          },
                        }
                      : {})}
                  />
                );
              })}
              {screenBoxes.flatMap((b) => {
                if (!b.label) return [];
                const isSelected = b.id === selectedBoxId;
                const lab = b.style?.label;
                // Visibility: per-box `show` (default true), the dense
                // declutter rule, and the `selectedOnly` mode.
                if (lab?.show === false) return [];
                if (labelsAutoHidden) return [];
                if (lab?.selectedOnly && !isSelected) return [];
                const boxColor = isSelected
                  ? (b.style?.activeColor ?? SELECTED_BOX_COLOR)
                  : (b.style?.color ?? b.color ?? DEFAULT_BOX_COLOR);
                const fontSize = lab?.fontSize ?? 12;
                const textColor = lab?.textColor ?? boxColor;
                const bgColor =
                  lab?.bgColor && lab.bgColor !== "box"
                    ? lab.bgColor
                    : boxColor;
                const bgOpacity = lab?.bgOpacity ?? 0;
                const padX = 4;
                const bgH = fontSize + 6;
                const bgW = b.label.length * fontSize * 0.6 + padX * 2;
                // Placement: above (default) / inside-top / auto.
                const pos = lab?.position ?? "above";
                const aboveY = b.screenY - bgH - 2;
                const useInside =
                  pos === "inside" || (pos === "auto" && aboveY < 0);
                const bgX = b.screenX;
                const bgY = useInside ? b.screenY + 2 : aboveY;
                const nodes = [];
                if (bgOpacity > 0) {
                  nodes.push(
                    <Rect
                      key={`${b.id}-label-bg`}
                      x={bgX}
                      y={bgY}
                      width={bgW}
                      height={bgH}
                      fill={hexToRgba(bgColor, bgOpacity)}
                      cornerRadius={2}
                      listening={false}
                    />,
                  );
                }
                nodes.push(
                  <Text
                    key={`${b.id}-label`}
                    x={bgX + padX}
                    y={bgY + 3}
                    text={b.label}
                    fontSize={fontSize}
                    fill={textColor}
                    listening={false}
                  />,
                );
                return nodes;
              })}
            </Layer>
          </Stage>
        </Box>
      ) : null}

      {/* SETTINGS-02 — dense-declutter notice (top-centre). Domain copy is
          supplied by the caller via `denseLabelNotice`. */}
      {labelsAutoHidden && denseLabelNotice ? (
        <Box
          data-testid="viewer-dense-label-notice"
          role="status"
          sx={{
            position: "absolute",
            top: 8,
            left: "50%",
            transform: "translateX(-50%)",
            px: 1.25,
            py: 0.5,
            borderRadius: 1,
            // A scrim over the photograph, so it is derived from the canvas
            // tokens rather than the page's `background.paper` — the notice
            // sits ON the image, not on the app surface.
            bgcolor: hexToRgba(CANVAS_SCRIM, 0.6),
            color: CANVAS_ON_SCRIM,
            fontSize: 12,
            pointerEvents: "none",
            zIndex: 6,
            whiteSpace: "nowrap",
          }}
        >
          {denseLabelNotice}
        </Box>
      ) : null}

      {marquee && (marquee.width > 0 || marquee.height > 0) ? (
        <Box
          aria-hidden="true"
          data-testid="viewer-zoom-marquee"
          sx={{
            position: "absolute",
            left: marquee.x,
            top: marquee.y,
            width: marquee.width,
            height: marquee.height,
            // SB6-ANA-VIEW-01 — 1 px dark outline + 1 px near-white inline
            // dashed border (via outline + dashed border on a
            // contrasting backing). Pairing the light neutral over the
            // scrim makes the marquee visible against both the bright
            // blade surface and the dark background, per Christian's
            // D1.Q2 lock — the pairing is what carries it, so both ends
            // come from the canvas tokens. The wash is the accent: a
            // marquee is a live interaction, which is precisely what P3
            // reserves aqua for (it was an arbitrary Windows blue).
            border: `1px dashed ${CANVAS_ON_SCRIM}`,
            outline: `1px solid ${hexToRgba(CANVAS_SCRIM, 0.85)}`,
            outlineOffset: "-1px",
            backgroundColor: hexToRgba(color.dark.primary, 0.15),
            pointerEvents: "none",
            // Above the OSD canvas + the Konva overlay.
            zIndex: 5,
          }}
        />
      ) : null}
    </Box>
  );
}
